import React, { ErrorInfo } from 'react';
import { forEach, merge } from 'ramda';
import { toast, ToastOptions } from 'react-toastify';
import { Auth } from 'aws-amplify';
import * as Sentry from '@sentry/browser';

import { checkUserType } from 'components/UserMgmt/UserMgmt.helpers';
import ToastContainer, { ToastType } from 'components/ToastContainer';

type FormattedError = {
  headerMessage: string;
  description: string;
};

type Props = {
  children: any;
};

type State = {
  hasError: boolean;
  error?: Error | undefined;
  errorInfo?: ErrorInfo | undefined;
  errors: FormattedError[];
};

const defaultErrorMessage = {
  displayMessage: 'Something went wrong',
  message: 'Not enough data to display at this time.',
};

export const toastMapping = (options: ToastOptions, type: ToastType) =>
  forEach(({ displayMessage, message }) =>
    toast(
      () => (
        <ToastContainer
          type={type}
          title={displayMessage}
          description={message}
        />
      ),
      merge(options, { hideProgressBar: true, closeButton: false }),
    ),
  );

// Sentry logging - Show dialog for internal users
export const sentryLogging = (error: Error, errorInfo?: ErrorInfo) => {
  if (process.env.STAGE === 'prod') {
    Sentry.withScope(async (scope) => {
      errorInfo && scope.setExtras(errorInfo as any);

      const currentUser = await Auth.currentAuthenticatedUser();
      const userType = checkUserType(currentUser.attributes);
      const eventId = Sentry.captureException(error);

      if (userType === 'dosh') {
        Sentry.showReportDialog({ eventId });
      }
    });
  }
};

class ErrorBoundary extends React.Component<Props, State> {
  state = {
    hasError: false,
    error: undefined,
    errorInfo: undefined,
    errors: [],
  };

  static getDerivedStateFromError({ message }: Error) {
    // Update state so the next render will show the fallback UI.
    try {
      return {
        errors: JSON.parse(message),
        hasError: true,
      };
    } catch (e) {
      return {
        errors: [defaultErrorMessage],
        hasError: true,
      };
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    sentryLogging(error, errorInfo);
  }

  render() {
    const { hasError, errors } = this.state;
    const { children } = this.props;

    if (hasError) {
      // GraphQL Errors are returned in an extra array
      if (Object.keys(errors[0]).indexOf('displayMessage') > -1) {
        toastMapping({}, 'error')(errors);
      }
      return null;
    }

    return children;
  }
}

export default ErrorBoundary;
